import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Auth } from "@aws-amplify/auth";
import { environment } from "../../../environments/environment";
import { successfulCognitoLoginAction } from "../../store/root/cognito-creds/cognito-creds.actions";
import { BaseHttpService } from './base.abstract';
import { HttpClient } from '@angular/common/http';
import {Observable, of, tap} from 'rxjs';
import { TokenResponse } from '../types/token.model';
import {
  Company,
  CompanyLogoUploadResponse,
  CompanyPostDto,
  CompanyResponse,
  CompanyThemeDTO,
  DecodedUserToken,
  User
} from "../types";
import { jwtDecode } from "jwt-decode";
import { objectToCamel } from "ts-case-convert";
import { Router } from "@angular/router";

@Injectable({
    providedIn: "root"
})
export class AuthService extends BaseHttpService {

  constructor(private store: Store, _httpClient: HttpClient, private router: Router) {
    super(_httpClient, `${environment.rootAuthnUrl}`)
  }
  private _companyThemes: CompanyThemeDTO[] | undefined;

    public async checkUserLoginStatusAndPermission(forceLogin = true, forceSSO = true): Promise<boolean> {
        try {
            const authObj = await Auth.currentAuthenticatedUser();
            this.store.dispatch(
                successfulCognitoLoginAction({
                    username: authObj.username,
                    email: authObj.signInUserSession.idToken.payload.email,
                    cognitoToken: authObj.signInUserSession.idToken.jwtToken
                }),
            );
            const token = localStorage.getItem('uiToken');
            if(token){
              const user: User = { ...objectToCamel(jwtDecode<DecodedUserToken>(token).data) };
              if(user.permissions!.find(value => value.permissionName === environment.adminPermissionName)){
                return true
              }else{
                this.router.createUrlTree(['unauthorized'])
              }
            }
          return false;
        } catch {
            if (forceLogin) {
                const signIn = await Auth.federatedSignIn(
                    forceSSO ? { customProvider: environment.cognito.customProviderName } : undefined,
                );
            }
            return false;
        }
    }

  public fetchMapToken(cognitoToken: string, email: string): Observable<TokenResponse> {
    return this.get<TokenResponse>(`token/${email}`, {
      headers: {"Authorization": `Bearer ${cognitoToken}`}
    }).pipe(
      tap(tokenResponse => {
        localStorage.setItem('uiToken', tokenResponse.token);
    }));
  }

  public getCompanies(): Observable<CompanyResponse> {
    return this.get<CompanyResponse>('company');
  }
  public getCompany(companyId: string): Observable<Company> {
    return this.get<Company>(`company/${companyId}`);
  }

  public addCompany(company: CompanyPostDto): Observable<CompanyPostDto> {
    return this.post<CompanyPostDto>(`company`, company);
  }

  public updateCompany(company: CompanyPostDto): Observable<CompanyPostDto> {
    return this.put<CompanyPostDto>(`company/${company.id}`, company);
  }
  public getThemes(): Observable<CompanyThemeDTO[]> {
    if(this._companyThemes){
      return of(this._companyThemes);
    }
    return this.get<CompanyThemeDTO[]>(`theme`).pipe(tap(themes=>this._companyThemes = themes));
  }
  public getTheme(themeId: string | undefined): Observable<CompanyThemeDTO | undefined> {
    if (!themeId) {
      return of(undefined);
    }
    return this.get<CompanyThemeDTO>(`theme/${themeId}`);
  }

  public updateTheme(theme: CompanyThemeDTO): Observable<CompanyThemeDTO | undefined> {
      const payload ={
        name: theme.name,
        hexCodes: theme.hexCodes
      }
    return this.put<CompanyThemeDTO>(`theme/${theme.id}`, payload);
  }

  public uploadCompanyLogo(companyId: string, formData: FormData): Observable<CompanyLogoUploadResponse> {
    return this.post<CompanyLogoUploadResponse>(`company/${companyId}/logo`, formData);
  }

  public getCompanyLogo(companyId: string): Observable<string> {
     return this.get<string>(`company/${companyId}/logo`);
   }

  public deleteCompanyLogo(companyId: string): Observable<void> {
    return this.delete<void>(`company/${companyId}/logo`);
  }

}
